import { useAtsCandidateDiscoveryApi } from '@/composables/useApi';
import type { PlacePrediction } from '@/core/shared/search/types/place-prediction.type';
import { InternalError } from '@/core/shared/errors/internal.error';
import { buildAddressPredictionsUrl } from '@/core/shared/search/utils/build-address-predictions-url.util';
import { isAbortError } from '@/core/shared/errors/abort-error.util';

export class SearchPersistence {
  // Latest-query-wins: a new lookup cancels this consumer's prior in-flight request, so the
  // autocomplete only ever resolves the most recent query. Abandoned in-flight requests (the
  // field cleared, the component unmounted) are left to complete by design — for cheap,
  // idempotent GETs like address predictions, cancel-on-leave isn't worth the wiring.
  private activeAddressPredictionAbort: (() => void) | null = null;

  public async getAddressPredictions(address: string): Promise<PlacePrediction[]> {
    this.activeAddressPredictionAbort?.();

    const url = buildAddressPredictionsUrl(address);
    const { execute, abort, data, error } = useAtsCandidateDiscoveryApi(url, {
      immediate: false,
    })
      .get()
      .json<PlacePrediction[]>();

    this.activeAddressPredictionAbort = abort;
    try {
      await execute();
    } finally {
      if (this.activeAddressPredictionAbort === abort) {
        this.activeAddressPredictionAbort = null;
      }
    }

    if (error.value) {
      // An aborted request was superseded by a newer lookup — control flow, not a failure.
      if (isAbortError(error.value)) {
        return [];
      }

      throw new InternalError('Could not fetch address predictions', {
        error: error.value,
        data: { address },
      });
    }

    return data.value ?? [];
  }
}
